import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, useLocation } from "react-router-dom";
import * as Sentry from "@sentry/browser";
import axios, { AxiosError } from "axios";

import { App as AntdApp, ConfigProvider } from "antd";
import { StyleProvider } from "@ant-design/cssinjs";
import en_US from "antd/locale/en_US";
import moment from "moment";

import "@/utils/dayjs";
import { PublicClientApplication } from "@azure/msal-browser";
import { broadcastResponseToMainFrame } from "@azure/msal-browser/redirect-bridge";
import {
  MsalProvider,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import { CookiesProvider } from "react-cookie";

import { LayoutContextProvider } from "@/containers/Apps/LayoutContext";
import { AppContextProvider } from "@/containers/Apps/AppContext";
import { AuthContextProvider } from "@/containers/Auth/AuthContext";
import InternalApp from "@/containers/Apps/InternalApp";
import PublicApp from "@/containers/Apps/PublicApp";
import AntdStaticBridge from "@/components/AntdStaticBridge";
import ErrorBoundary from "@/components/ErrorBoundary";
import LocationCatcher from "@/containers/Auth/LocationCatcher";
import { getEnvConfig } from "@/env";
import { isReauthReloadPending } from "@/utils/api";

import "moment/locale/es-us";

import "@/static/styles/theme.less";

import "@/static/styles/base.less";
import "@/static/styles/layout.less";
import "@/static/styles/datatables.less";
import "@/static/styles/modals.less";
import "@/static/styles/forms.less";
import "@/static/styles/nav.less";
import "@/static/styles/typo.less";
import "@/static/styles/reset.less";
import "@/static/styles/draganddrop.less";
import "@/static/styles/animations.less";

import "@/static/styles/auth/base.less";
import "@/static/styles/dashboard/base.less";
import "@/static/styles/support/base.less";
import "@/static/styles/services/service_comments.less";
import "@/static/styles/sim_cards/sim_cards.less";
import "@/static/styles/orders/terminate.less";

moment.locale("en");

const isBrowserTest = localStorage.getItem("is_browser_test") === "true";
const envConfig = getEnvConfig();
const disableAzureAuth = isBrowserTest && envConfig.environment === "local";

// MSAL v5 returns silent (hidden iframe) and popup auth responses through a
// "redirect bridge": the page at redirectUri must call
// broadcastResponseToMainFrame() to hand the response to the main window. Our
// redirectUri (/auth-callback) is this SPA, so inside MSAL's hidden iframe the
// whole app booted instead: the response was never handed back (the silent
// call timed out) and the app's sign-in fallback tried to redirect from inside
// the iframe (block_iframe_reload). When this document is that iframe, act as
// the bridge and stop here, before the app boots. Top-level redirects back to
// /auth-callback are still handled by MSAL inside the app.
const isMsalRedirectBridge = (() => {
  if (window.parent === window) {
    return false;
  }
  const trimSlashes = (path: string) => path.replace(/\/+$/, "");
  const redirectPath = new URL(
    envConfig.azureAuth.auth.redirectUri,
    window.location.origin,
  ).pathname;
  if (trimSlashes(window.location.pathname) !== trimSlashes(redirectPath)) {
    return false;
  }
  // MSAL's own test for an auth response: a `state` param in hash or query.
  return [window.location.hash, window.location.search].some((part) =>
    new URLSearchParams(part.slice(1)).has("state"),
  );
})();

if (isMsalRedirectBridge) {
  await broadcastResponseToMainFrame().catch((error) => {
    console.error("Unable to hand the auth response to the main window", error);
  });
  // Never settles: nothing below (MSAL, Sentry, React) runs in this frame.
  await new Promise(() => {});
}

const publicClientApplication = new PublicClientApplication(
  envConfig.azureAuth,
);

await publicClientApplication.initialize();

// After a deploy, a tab that is still open references the previous build's
// hashed chunks, which no longer exist on the server. Vite fires
// `vite:preloadError` when a chunk or its CSS fails to load: reload once so
// the tab picks up the new build. The timestamp guard stops a reload loop when
// an asset is genuinely missing (and without sessionStorage there is no guard,
// so don't reload at all); the error then reaches the error boundary as before.
const STALE_CHUNK_RELOAD_KEY = "stale_chunk_reload_at";
const STALE_CHUNK_RELOAD_WINDOW_MS = 10_000;
let staleChunkReloadPending = false;

const claimStaleChunkReload = (): boolean => {
  let lastReloadAt: number;
  try {
    lastReloadAt = Number(sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY));
  } catch {
    return false;
  }
  if (Date.now() - lastReloadAt < STALE_CHUNK_RELOAD_WINDOW_MS) {
    return false;
  }
  try {
    sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }
  return true;
};

window.addEventListener("vite:preloadError", () => {
  if (staleChunkReloadPending || !claimStaleChunkReload()) {
    return;
  }
  staleChunkReloadPending = true;
  window.location.reload();
});

// Request failures that say nothing about the app. A 401 is handled by the
// reauth interceptor in utils/api (it reloads into the MSAL boot flow). A
// request that got no response because the browser is offline, or because the
// page is reloading and the browser ended its in-flight requests, is expected.
// Other network errors (e.g. a gateway timeout without CORS headers) and every
// 5xx are still reported.
const isExpectedRequestFailure = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false;
  }
  if (error.response?.status === 401) {
    return true;
  }
  return (
    error.code === AxiosError.ERR_NETWORK &&
    (!navigator.onLine || isReauthReloadPending() || staleChunkReloadPending)
  );
};

if (envConfig.sentryDSN) {
  Sentry.init({
    dsn: envConfig.sentryDSN,
    release: envConfig.appVersion,
    environment: envConfig.environment || "development",
    ignoreErrors: [
      // Ignore errors from failed module imports
      "Failed to fetch dynamically imported module",
      "ChunkLoadError",
      "Loading chunk",
      "Loading CSS chunk",
      "Unable to preload CSS",
    ],
    beforeSend(event, hint) {
      if (isExpectedRequestFailure(hint.originalException)) {
        return null;
      }
      // Filter out errors related to module loading
      const error = hint.originalException || hint.syntheticException;
      if (error && typeof error === "object" && "message" in error) {
        const message = String(error.message).toLowerCase();
        if (
          message.includes("failed to fetch dynamically imported module") ||
          message.includes("chunkloaderror") ||
          message.includes("loading chunk") ||
          message.includes("unable to preload css")
        ) {
          return null; // Don't send this event
        }
      }
      return event;
    },
    // Limit the number of events sent to avoid rate limiting
    sampleRate: 1.0,
    maxBreadcrumbs: 50,
  });
}

const themeConfig = {
  token: {
    // Colors
    colorPrimary: "#0064cf",
    colorInfo: "#0064cf",
    colorSuccess: "rgb(116, 200, 84)",
    colorError: "#f5222d",
    colorWarning: "#faad14",
    colorBgBase: "#ffffff",
    colorBgLayout: "#f0f0f7",
    colorBorder: "#d9d9d9",
    colorTextSecondary: "#929292",

    // Typography
    fontFamily: "'Fira Sans', Arial, sans-serif",
    fontSize: 14,

    // Border radius
    borderRadius: 4,
    borderRadiusLG: 6,
    borderRadiusSM: 2,
    borderRadiusXS: 2,
  },
  components: {
    Table: {
      cellPaddingBlock: 6,
      cellPaddingBlockMD: 6,
      cellPaddingBlockSM: 4,
      cellPaddingInline: 8,
      cellPaddingInlineMD: 8,
      cellPaddingInlineSM: 4,
      rowHoverBg: "#f0f5ff",
    },
    Layout: {
      headerPadding: "0 25px",
      headerBg: "#0064cf",
      headerColor: "#fff",
      footerPadding: "7px 17px",
      footerBg: "#f5f5f5",
      bodyBg: "#f0f0f7",
      // siderBg not set — allows dark-themed siders to keep their dark background
    },
    Menu: {
      activeBarWidth: 3,
      darkItemBg: "#0064cf",
      darkSubMenuItemBg: "#ffffff",
      itemBg: "transparent",
      subMenuItemBg: "#f5f5f5",
      itemHoverBg: "#e0f2ff",
      itemSelectedBg: "#d3e0ec5e",
      itemMarginBlock: 0,
      itemMarginInline: 0,
      itemPaddingInline: 24,
    },
    Form: {
      labelFontSize: 14,
      labelColor: "rgba(0, 0, 0, 0.88)",
      itemMarginBottom: 16,
      verticalLabelPadding: "0 0 4px",
    },
    Modal: {
      paddingContentHorizontalLG: 24,
      titleFontSize: 16,
      titleLineHeight: 1.5,
    },
    Collapse: {
      contentBg: "#ffffff",
      headerBg: "#ffffff",
      contentPadding: "0 10px",
      borderRadiusLG: 2,
    },
    Card: {
      headerBg: "transparent",
      headerFontSize: 14,
      headerHeight: 40,
    },
    Descriptions: {
      labelBg: "#fafafa",
    },
    Pagination: {
      colorPrimary: "#0064cf",
      colorPrimaryHover: "#0056b3",
    },
    Popover: {
      zIndexPopup: 1030,
    },
    Dropdown: {
      zIndexPopup: 1050,
    },
  },
};

// App-wide error boundary keyed on the router location, so navigating to a
// different route auto-clears a stuck error instead of trapping the user on
// the fallback (with no nav chrome) until a hard reload.
const AppErrorBoundary = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  return (
    <ErrorBoundary boundaryName="app-root" resetKeys={[location.pathname]}>
      {children}
    </ErrorBoundary>
  );
};

const root = createRoot(document.getElementById("root")!);
root.render(
  <BrowserRouter>
    <LocationCatcher />
    <CookiesProvider>
      <LayoutContextProvider>
        <StyleProvider hashPriority="low">
          <ConfigProvider locale={en_US} theme={themeConfig}>
            <AntdApp component={false}>
              <AntdStaticBridge />
              <AppErrorBoundary>
                <AppContextProvider>
                  {disableAzureAuth ? (
                    <AuthContextProvider>
                      <InternalApp />
                    </AuthContextProvider>
                  ) : (
                    <MsalProvider instance={publicClientApplication}>
                      <AuthContextProvider>
                        <AuthenticatedTemplate>
                          <InternalApp />
                        </AuthenticatedTemplate>
                        <UnauthenticatedTemplate>
                          <PublicApp />
                        </UnauthenticatedTemplate>
                      </AuthContextProvider>
                    </MsalProvider>
                  )}
                </AppContextProvider>
              </AppErrorBoundary>
            </AntdApp>
          </ConfigProvider>
        </StyleProvider>
      </LayoutContextProvider>
    </CookiesProvider>
  </BrowserRouter>,
);
